import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import Button from "@/components/ui/Button";
import { memo } from "react";
const AboutSection = ({ seedData, lng }) => {
  const navigate = useNavigate();
  const about = seedData?.about;

  const getText = (obj, key) => obj?.[`${key}_${lng}`] || obj?.[`${key}_en`];

  return (
    <section id="about" className="relative py-24 bg-white overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-20 -left-32 w-72 h-72 bg-gradient-to-r from-blue-200/30 to-cyan-300/20 rounded-full blur-3xl animate-blob"></div>
        <div className="absolute -bottom-24 -right-20 w-96 h-96 bg-gradient-to-r from-cyan-200/30 to-blue-300/20 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.8)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.8)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-3 mb-4 animate-fade-in">
            <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
            <span className="text-blue-600 font-semibold uppercase tracking-wider text-sm">
              {lng === "ar" ? "من نحن" : "About Us"}
            </span>
            <div className="w-3 h-3 bg-cyan-500 rounded-full animate-pulse animation-delay-1000"></div>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 animate-fade-in">
            {getText(about, "name")}
          </h2>
          <div className="flex justify-center items-center gap-4 animate-fade-in">
            <div className="h-1 w-12 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full"></div>
            <div className="h-1 w-20 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full"></div>
            <div className="h-1 w-12 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full"></div>
          </div>
        </div>

        <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
          <div className="space-y-8 animate-slide-up">
            <div className="relative">
              <div className="absolute -left-6 top-0 w-1 h-full bg-gradient-to-b from-blue-400 to-cyan-400 rounded-full"></div>
              <p className="text-xl md:text-2xl lg:text-3xl text-gray-800 leading-relaxed font-light min-h-[8rem]">
                {getText(about, "description")}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-6 py-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  496+
                </div>
                <div className="text-gray-600 font-medium">
                  {lng === "ar" ? "مشروع مكتمل" : "Projects Done"}
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-cyan-600 mb-2">
                  26+
                </div>
                <div className="text-gray-600 font-medium">
                  {lng === "ar" ? " محافظة مخدومة" : "Governorate"}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mt-8 min-h-[3.5rem]">
              <Button
                onClick={() => navigate("/about")}
                className="group relative bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-8 py-4 rounded-2xl font-semibold shadow-2xl transform hover:scale-105 transition-all duration-300 overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-2">
                  {lng === "ar" ? "اكتشف المزيد" : "Discover More"}
                  <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-300 rtl:rotate-180" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Button>

              <Link
                to="/gallery"
                className="group inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-gray-200 hover:border-blue-300 text-blue-600 font-semibold hover:text-blue-800 hover:shadow-lg px-6 py-4 rounded-2xl transition-all duration-300"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span>{lng === "ar" ? "تصفح المعرض" : "Browse Gallery"}</span>
                <div className="w-0 group-hover:w-4 h-0.5 bg-blue-600 transition-all duration-300"></div>
              </Link>
            </div>
          </div>

          <div className="relative mt-16 lg:mt-0 flex justify-center lg:justify-end">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-3xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity duration-500"></div>

              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white transform hover:scale-105 hover:-rotate-1 transition-all duration-500 w-full max-w-md h-96 aspect-[4/3] group">
                <img
                  src="logo.webp"
                  alt="Modern Facility"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                  decoding="async"
                  width="400"
                  height="300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>

              <div className="absolute -top-6 -right-6 w-32 h-32 rounded-2xl border-4 border-white bg-white/20 backdrop-blur-md shadow-2xl transform hover:scale-110 hover:rotate-6 transition-all duration-500 hidden md:block">
                <img
                  src="NSPObuilding03.webp"
                  alt="Digital"
                  className="w-full h-full object-cover rounded-xl"
                  loading="lazy"
                  decoding="async"
                  width="128"
                  height="128"
                />
              </div>

              <div className="absolute -bottom-8 -left-8 w-24 h-24 rounded-xl border-4 border-white bg-white/20 backdrop-blur-md shadow-2xl transform hover:scale-110 hover:-rotate-12 transition-all duration-500 hidden md:block">
                <div className="w-full h-full bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center">
                  <img
                    src="111.webp"
                    alt="Digital"
                    className="w-full h-full object-cover rounded-xl"
                    loading="lazy"
                    decoding="async"
                    width="128"
                    height="128"
                  />
                </div>
              </div>

              <div className="absolute -bottom-4 right-1/4 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full shadow-lg border border-gray-100">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-semibold text-gray-700">
                    {lng === "ar" ? "متميزون" : "Premium"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default memo(AboutSection);